import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { defaultTableData } from "store/config/constants";
import { EBaseUrl } from "index";
import thunkOptions from "store/config/thunkOptions";
import { IGridRequest, IPaginated, IState } from "store/interfaces/main";
import { api } from "store/services/apiService";
import {
  ICustomer,
  ICustomersState,
  ICreateOneTimeTrigger,
  IChangeCustomerStatus, IChangeCustomerAllStatuses, IChangeCustomerSelectedStatuses,
} from "../interfaces/customers";

const name = "CUSTOMERS";

const initialState: ICustomersState = {
  listData: defaultTableData,
};

export const GetCustomers = createAsyncThunk<
  IPaginated<ICustomer>,
  IGridRequest
>(
  `${name}/GetCustomers`,
  async (formData: IGridRequest) => {
    return (await api.post(`${EBaseUrl.API}/Customer/Grid`, formData)).data;
  },
  thunkOptions
);

export const ChangeCustomerStatus = createAsyncThunk<
  unknown,
  IChangeCustomerStatus
>(
  `${name}/ChangeCustomerStatus`,
  async (data: IChangeCustomerStatus) => {
    return (
      await api.put(`${EBaseUrl.API}/Customer/Status/${data.id}`, data.formData)
    ).data;
  },
  thunkOptions
);

export const ChangeCustomerAllStatuses = createAsyncThunk<
  unknown,
  IChangeCustomerAllStatuses
>(
  `${name}/ChangeCustomerStatusAll`,
  async (data) => {
    return (
      await api.put(`${EBaseUrl.API}/Customer/Status/All`, data)
    ).data;
  },
  thunkOptions
);


export const ChangeCustomerStatusSelected = createAsyncThunk<
  unknown,
  IChangeCustomerSelectedStatuses
>(
  `${name}/ChangeCustomerStatusSelected`,
  async (data: IChangeCustomerSelectedStatuses) => {
    return (
      await api.put(`${EBaseUrl.API}/Customer/Status/Selected`, data)
    ).data;
  },
  thunkOptions
);


export const ExportCustomers = createAsyncThunk<
  string,
  { customerIDs: number[] }
>(
  `${name}/ExportCustomers`,
  async (formData) => {
    return await api.post(`${EBaseUrl.API}/Customer/Export`, formData, {
      responseType: "blob",
    });
  },
  thunkOptions
);

export const CustomersExport = createAsyncThunk<ICustomer[], string>(
  `${name}/CustomersExport`,
  async (params) => {
    return (await api.get(`${EBaseUrl.API}/admin/customers/export?${params}`))
      .data;
  },
  thunkOptions
);

export const BlockCustomer = createAsyncThunk<unknown, number>(
  `${name}/BlockCustomer`,
  async (id) => {
    return (await api.put(`${EBaseUrl.API}/admin/customers/${id}/block`)).data;
  },
  thunkOptions
);

export const UnblockCustomer = createAsyncThunk<unknown, number>(
  `${name}/UnblockCustomer`,
  async (id) => {
    return (await api.put(`${EBaseUrl.API}/admin/customers/${id}/unblock`))
      .data;
  },
  thunkOptions
);

export const CreateOneTimeTrigger = createAsyncThunk<
  unknown,
  ICreateOneTimeTrigger
>(
  `${name}/CreateOneTimeTrigger`,
  async (formData) => {
    return (await api.post(`${EBaseUrl.API}/createCustomCustomers`, formData))
      .data;
  },
  thunkOptions
);

export const CancelOneTimeTrigger = createAsyncThunk<unknown, string>(
  `${name}/CancelOneTimeTrigger`,
  async (campaignId: string) => {
    return (
      await api.delete(
        `${EBaseUrl.API}/Campaign/cancelOneTimeSurvey?campaignId=${campaignId}`
      )
    ).data;
  },
  thunkOptions
);

export const GetOneTimeSurvey = createAsyncThunk<unknown, string>(
  `${name}/GetOneTimeSurvey`,
  async (campaignId: string) => {
    return (
      await api.get(
        `${EBaseUrl.API}/Campaign/GetOneTimeSurvey?campaignId=${campaignId}`
      )
    ).data;
  },
  thunkOptions
);

export const RescheduleOneTimeTrigger = createAsyncThunk<
  unknown,
  { campaignId: string; scheduleDateTime: string }
>(
  `${name}/RescheduleOneTimeTrigger`,
  async ({ campaignId, scheduleDateTime }) => {
    return (
      await api.put(
        `${EBaseUrl.API}/Campaign/rescheduleOneTimeSurvey?campaignId=${campaignId}&scheduleDateTime=${scheduleDateTime}`
      )
    ).data;
  },
  thunkOptions
);

const customersSlice = createSlice({
  initialState,
  name,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(GetCustomers.fulfilled, (state, { payload }) => {
      state.listData = payload;
    });
  },
});

export const selectCustomers = (state: IState) => state.customers.listData;

export default customersSlice.reducer;
